import { memo } from 'react';
import { cn } from '@/lib/utils';
import { Shield, MessageSquare, Printer, Check } from 'lucide-react';
import type { DIDLocator } from '../types';

interface DIDCardProps {
  did: DIDLocator;
  isSelected: boolean;
  onSelect: (tn: string) => void;
}

// Memoized to prevent re-renders when parent state changes but card props are unchanged
export const DIDCard = memo(function DIDCard({ did, isSelected, onSelect }: DIDCardProps) {
  // Format phone number for display
  const formatPhone = (tn: string): string => {
    const cleaned = tn.replace(/\D/g, '');
    const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return tn;
  };

  // Format location
  const location = [did.rate_center, did.state].filter(Boolean).join(' \u2022 ');

  return (
    <div
      onClick={() => onSelect(did.tn)}
      className={cn(
        'relative bg-white rounded-xl border-2 p-4 cursor-pointer transition-all duration-200',
        isSelected
          ? 'border-indigo-500 bg-indigo-50/30 shadow-md'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
      )}
    >
      {/* Selection indicator */}
      <div
        className={cn(
          'absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center transition-all',
          isSelected
            ? 'bg-indigo-500 text-white'
            : 'bg-slate-100 text-slate-300'
        )}
      >
        <Check size={14} strokeWidth={3} />
      </div>

      {/* Availability label */}
      <div className="mb-3">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          Available
        </span>
      </div>

      {/* Phone number */}
      <div className="mb-2">
        <span className="font-mono text-lg font-semibold text-slate-900">
          {formatPhone(did.tn)}
        </span>
      </div>

      {/* Location */}
      <div className="mb-4">
        <span className="text-sm text-slate-500">{location || 'Unknown'}</span>
      </div>

      {/* Service icons and tier badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* E911 */}
          <div
            className="p-1.5 rounded bg-slate-100 text-slate-400"
            title="E911 Available"
          >
            <Shield size={14} />
          </div>
          {/* SMS */}
          <div
            className={cn(
              'p-1.5 rounded',
              did.sms ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-300'
            )}
            title={did.sms ? 'SMS Enabled' : 'SMS Not Available'}
          >
            <MessageSquare size={14} />
          </div>
          {/* Fax/T38 */}
          <div
            className={cn(
              'p-1.5 rounded',
              did.t38 ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-300'
            )}
            title={did.t38 ? 'T.38 Fax Enabled' : 'T.38 Not Available'}
          >
            <Printer size={14} />
          </div>
        </div>

        {/* Tier badge */}
        {did.tier && (
          <span className="bg-slate-100 border border-slate-200 text-slate-500 rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase">
            {did.tier.toUpperCase()}
          </span>
        )}
      </div>
    </div>
  );
});

// Grid container for DID cards
interface DIDCardGridProps {
  children: React.ReactNode;
  className?: string;
}

export function DIDCardGrid({ children, className }: DIDCardGridProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
        className
      )}
    >
      {children}
    </div>
  );
}
